import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CAREERS_SECTION_KEYS,
  fetchCareersPage,
  findSection,
} from "../services/contentApi";

const CareersPageContext = createContext(null);

export function CareersPageProvider({ children }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchCareersPage()
      .then((data) => {
        if (!cancelled) {
          setPage(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setPage(null);
        }
        console.error("Error fetching careers page:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(() => {
    const result = {};
    CAREERS_SECTION_KEYS.forEach((key) => {
      result[key] = findSection(page, key);
    });
    if (!result["open-positions"] && page?.sections) {
      result["open-positions"] =
        page.sections.find(
          (s) =>
            (s.sectionType || s.type || "").toLowerCase() === "positions"
        ) ?? null;
    }
    return result;
  }, [page]);

  const value = useMemo(
    () => ({ page, sections, loading, error }),
    [page, sections, loading, error]
  );

  return (
    <CareersPageContext.Provider value={value}>
      {children}
    </CareersPageContext.Provider>
  );
}

export function useCareersPage() {
  const ctx = useContext(CareersPageContext);
  if (!ctx) {
    throw new Error("useCareersPage must be used within CareersPageProvider");
  }
  return ctx;
}

export function useCareersSection(key) {
  const ctx = useContext(CareersPageContext);
  if (!ctx) {
    return { section: null, loading: false, fromCareersPage: false };
  }
  return {
    section: ctx.sections[key] ?? null,
    loading: ctx.loading,
    fromCareersPage: true,
  };
}
